/* eslint-disable max-len */
import * as React from 'react';
import { StylesProps } from 'theme/jss-types';
import {
    Button,
    Checkbox,
    message,
    Modal,
    Switch,
} from 'antd';
import { Header } from './settings.types';
import { updateHeaders, updatePieceHeaders, updateInvoiceHeaders } from 'network/settings-product';
import { editHeadersStyles } from './edit-headers.styles';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import Search from 'antd/lib/transfer/search';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { CloseOutlined } from '@ant-design/icons';
import generateColumnNames from './generateColumnNames';
import { ReduxStore } from '../../reducers/redux.types';
import { HocOptions } from '../common/generic-hoc.types';
import GenericHoc from '../common/generic-hoc';

interface ResetHeadersProps extends StylesProps<ReturnType<typeof editHeadersStyles>> {
    headers: Header[];
    orgMandatoryHeaders: Header[];
    onClose: (reload?: boolean) => void;
    freezeDefaultMandatoryHeaders?: boolean;
    headerType?: string;
    editVisible: boolean;
    allowCustomerChoseMandatoryHeaders: boolean;
}
const EditHeaders = (props: ResetHeadersProps) => {
    const {
        classes,
        onClose,
        headers: allHeaders,
        orgMandatoryHeaders,
        freezeDefaultMandatoryHeaders,
        headerType,
        editVisible,
        allowCustomerChoseMandatoryHeaders,
    } = props;
    const { t } = useTranslation();
    const [saving, setSaving] = React.useState<boolean>(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const configuredHeaders = useMemo(() => {
        return allHeaders?.filter((header: Header) => header?.positionAlpha !== 'Not Present')
            ?.sort((a, b) => {
                const aLen = a.positionAlpha.length;
                const bLen = b.positionAlpha.length;
                if (aLen !== bLen) {
                    return aLen - bLen;
                }
                return a.positionAlpha.localeCompare(b.positionAlpha);
            });
    }, [allHeaders]);

    const [orderedHeaders, setOrderedHeaders] = React.useState<Header[]>(configuredHeaders);
    const columnNames = generateColumnNames(orderedHeaders.length);

    const filteredColumns = useMemo(
        () => {
            const filtered = allHeaders.filter((column) => {
                const headerText = column?.name?.toLowerCase() || '';
                const query = searchQuery?.toLowerCase() || '';
                return !query || headerText.includes(query);
            });

            const configuredSet = new Set(configuredHeaders.map((h) => h.id));

            return filtered.sort((a, b) => {
                const aInConfigured = configuredSet.has(a.id);
                const bInConfigured = configuredSet.has(b.id);

                if (aInConfigured && !bInConfigured) return -1;
                if (!aInConfigured && bInConfigured) return 1;
                return 0;
            });
        },
        [allHeaders, searchQuery, configuredHeaders],
    );

    const handleCheckboxChange = (column: Header, checked: boolean) => {
        setOrderedHeaders((prev) => {
            return checked
                ? [...prev, column]
                : prev.filter((item) => item.id !== column.id);
        });
    };

    const DragHandle = () => (
        <div className={classes.dragHandleStyle} aria-label="Drag handle">
            <span className={classes.dragIconStyle} />
        </div>
    );

    const handleRemoveColumn = (column : Header) => {
        setOrderedHeaders((prev) => prev.filter((col) => col.id !== column.id));
    };

    const handleMandatoryChange = (checked: boolean, header: Header) => {
        const index = orderedHeaders.findIndex((item) => item.id === header.id);
        if (index === -1) return;
        const newHeaders = [...orderedHeaders];
        newHeaders[index] = {
            ...newHeaders[index],
            mandatory: checked,
        };

        setOrderedHeaders(newHeaders);
    };

    const renderMandatory = (header: Header) => {
        const orgHeader = freezeDefaultMandatoryHeaders
            ? orgMandatoryHeaders.find((h) => h.id === header.id)
            : undefined;

        const isMandatory = header.mandatory || (freezeDefaultMandatoryHeaders && orgHeader?.mandatory);
        const isDisabled = freezeDefaultMandatoryHeaders && orgHeader?.mandatory;

        return (
            <Switch
                key={header.id}
                onChange={(e) => handleMandatoryChange(e, header)}
                checked={isMandatory}
                disabled={isDisabled}
                className={classes.mandatorySwitch}
            />
        );
    };

    const renderRemoveButton = (header: Header) => {
        return (
            <Button
                onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveColumn(header);
                }}
                className={classes.crossButton}
                aria-label={`Remove ${header.name}`}
                icon={<CloseOutlined />}
            />
        );
    };

    const renderHeaderName = (header: Header) => {
        return (
            <span
                className={classes.headerElementStyle}
                title={header.name}
                aria-label={header.name}
            >
                {header.name}
            </span>
        );
    };

    interface SortableItemProps {
        header: Header;
        index: number;
    }

    const SortableItem = SortableElement(({ header }: SortableItemProps) => {
        return (
            <div className={classes.headerElement}>
                <div className={classes.sortableItemStyle}>
                    <DragHandle />
                    {renderHeaderName(header)}
                    {renderRemoveButton(header)}
                </div>
                {allowCustomerChoseMandatoryHeaders && renderMandatory(header)}
            </div>
        );
    });

    const SortableList = SortableContainer(({ items }: any) => (
        <div>
            {items.map((item: any, index: number) => (
                <SortableItem
                    key={`item-${item.id}`}
                    index={index}
                    header={item}
                />
            ))}
        </div>
    ));

    const renderSearchBar = () => {
        return (
            <div className={classes.searchStyle}>
                <Search
                    onChange={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
                    placeholder={t('Search')}
                />
            </div>
        );
    };

    const renderAllHeaders = () => {
        return (
            <div className={classes.checkboxDiv}>
                {filteredColumns.length > 0 ? (
                    filteredColumns.map((column) => {
                        return (
                            <div className={classes.checkBoxStyle} key={column.id}>
                                <Checkbox
                                    checked={orderedHeaders.some((header) => header?.id === column?.id)}
                                    onChange={(e) => handleCheckboxChange(column, e.target.checked)}
                                    aria-label={`Toggle ${column.name}`}
                                >
                                    {column.name}
                                </Checkbox>
                            </div>
                        );
                    })
                ) : (
                    <div className={classes.noResultsText}>{t('no_results_found')}</div>
                )}
            </div>
        );
    };


    const renderRightDivHeadings = () => {
        return (
            <div className={classes.rightDivStickyHeader}>
                <div className={classes.rightDivColumnHeader}>{t('column_xls')}</div>
                <div className={classes.rightDivListHeader}>{t('headers')}</div>
                {allowCustomerChoseMandatoryHeaders && <div className={classes.rightDivColumnMandatory}>{t('mandatory')}</div>}
            </div>
        );
    };

    const renderColumnNames = () => {
        return (
            <div className={classes.rightDivColumnElement}>
                {orderedHeaders.map((_, index) => (
                    <div className={classes.columnStyle}>
                        {columnNames[index]}
                    </div>
                ))}
            </div>
        );
    };

    interface handleDragEndProps {
        oldIndex: number;
        newIndex: number;
    }

    const handleDragEnd = ({ oldIndex, newIndex }: handleDragEndProps) => {
        if (oldIndex === newIndex) return;
        const updatedOrderedColumns = [...orderedHeaders];
        const [movedItem] = updatedOrderedColumns.splice(oldIndex, 1);
        updatedOrderedColumns.splice(newIndex, 0, movedItem);
        setOrderedHeaders(updatedOrderedColumns);
    };

    const renderSortableList = () => {
        return (
            <div className={classes.rightDivListElement}>
                <SortableList
                    items={orderedHeaders}
                    onSortEnd={handleDragEnd}
                    lockAxis="y"
                    helperClass="sortableHelper"
                />
            </div>
        );
    };

    const renderModalBody = () => {
        return (
            <div className={classes.mainDiv}>
                <div className={classes.leftDiv}>
                    {renderSearchBar()}
                    {renderAllHeaders()}
                </div>
                <div className={classes.rightDiv}>
                    {renderRightDivHeadings()}
                    <div className={classes.rightDivScrollElement}>
                        {orderedHeaders.length === 0 ? (
                            <div className={classes.noResultsText}>{t('no_columns_selected')}</div>
                        ) : (
                            <>
                                {renderColumnNames()}
                                {renderSortableList()}
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const excelColumnToNumber = (label: string) => label.split('').reduce((result, char) => result * 26 + (char.charCodeAt(0) - 64), 0) - 1;

    const getApiBody = () => {
        return allHeaders.map((item) => {
            const orderedIndex = orderedHeaders.findIndex((h) => h.id === item.id);
            const isPresent = orderedIndex !== -1;
            return {
                ...item,
                mandatory: isPresent ? orderedHeaders[orderedIndex].mandatory : item.mandatory,
                pos: {
                    position: isPresent ? excelColumnToNumber(columnNames[orderedIndex]) : -1,
                    positionAlpha: isPresent ? columnNames[orderedIndex] || '' : 'Not Present',
                },
                position: isPresent ? excelColumnToNumber(columnNames[orderedIndex]) : -1,
                positionAlpha: isPresent ? columnNames[orderedIndex] || '' : 'Not Present',
            };
        });
    };

    const saveHeaders = async () => {
        setSaving(true);
        const apiBody = getApiBody();
        let response;
        switch (headerType) {
            case 'piece':
                response = await updatePieceHeaders(apiBody);
                break;
            case 'invoice':
                response = await updateInvoiceHeaders(apiBody);
                break;
            default:
                response = await updateHeaders(apiBody);
        }
        if (response.isSuccess) {
            message.success(t('header_mapping_update_success'));
            onClose(true);
        } else {
            message.error(response.errorMessage);
        }
        setSaving(false);
    };

    const renderFooter = () => {
        return (
            <Button
                type="primary"
                loading={saving}
                onClick={() => saveHeaders()}
                disabled={orderedHeaders?.length === 0}
            >
                {t('save')}
            </Button>
        );
    };

    const renderHeader = () => {
        return (
            <div className={classes.editModalHeader}>
                {t('edit_mapping')}
                <CloseOutlined onClick={() => onClose()} />
            </div>
        );
    };

    return (
        <Modal
            className={classes.editModal}
            open={editVisible}
            closable={false}
            title={renderHeader()}
            width="70%"
            footer={renderFooter()}
            style={{ pointerEvents: saving ? 'none' : 'auto' }}
        >
            {renderModalBody()}
        </Modal>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    return {
        allowCustomerChoseMandatoryHeaders: state.master.parts_to_show?.allow_customer_choosen_mandatory_headers,
    };
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: editHeadersStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

const EditHeadersPage = GenericHoc(hocConfig)(EditHeaders);

export default EditHeadersPage;
